import Alpine from './alpine'

/**
 * The operator's product form: option names, and the rows they describe.
 *
 * Registered here rather than in the view that uses it. A component registered
 * from a pushed script is registered after Alpine has already started, because
 * the entry module calls start() the moment it runs - so the listener is added
 * to an event that has been and gone, x-data resolves to nothing, and every
 * control on the form silently does nothing at all. "Add variant" was dead for
 * exactly that reason, and so were the option columns.
 */
Alpine.data('masterProductForm', ({ optionNames = [], isNew = false } = {}) => ({
    optionNames: [optionNames[0] ?? '', optionNames[1] ?? '', optionNames[2] ?? ''],
    isNew,

    /**
     * Only the options that have been named.
     *
     * The variant rows show a column per one of these, so an unnamed option is
     * a column of empty boxes asking to be filled in.
     */
    get namedOptions() {
        return this.optionNames.filter((name) => (name ?? '').trim() !== '')
    },

    /**
     * $root, not $el.
     *
     * Alpine resolves $el where the expression runs, so inside a method called
     * from a button's @click it is the button - and a button contains no rows.
     * $root is the element the component is mounted on, which is the form.
     */
    rows() {
        return Array.from(this.$root.querySelectorAll('[data-variant-row]'))
    },

    addRow() {
        const rows = this.rows()
        const last = rows[rows.length - 1]
        const copy = last.cloneNode(true)
        const index = rows.length

        /*
         * A fresh row, not a copy of the one above with its id attached. That
         * id belongs to a variant that already exists, and submitting it twice
         * would have the second row overwrite the first.
         */
        copy.querySelectorAll('input, select').forEach((field) => {
            if (field.name.endsWith('[id]')) {
                field.remove()
                return
            }

            field.name = field.name.replace(/variants\[\d+\]/, `variants[${index}]`)

            if (field.tagName === 'SELECT') {
                field.selectedIndex = 0
            } else if (field.type !== 'hidden') {
                field.value = ''
            }
        })

        last.after(copy)
        copy.querySelector('input')?.focus()
    },

    removeRow(event) {
        /*
         * Never the last one. A product with no variants cannot hold stock, and
         * stock is the reason these records exist at all.
         */
        if (this.rows().length <= 1) {
            return
        }

        event.target.closest('[data-variant-row]').remove()
        this.renumber()
    },

    /** Names carry the row's position, so removing one shifts everything after it. */
    renumber() {
        this.rows().forEach((row, index) => {
            row.querySelectorAll('input, select').forEach((field) => {
                field.name = field.name.replace(/variants\[\d+\]/, `variants[${index}]`)
            })
        })
    },
}))
